import { STATE } from '../constants/stateClasses';
import { breakpoints } from '../utils/breakpoints';
import { debounce } from '../utils/debounce';
import toggleClass from '../utils/toggleClass';
import { scroll } from './locomotive';

const handleRecipeFilter = () => {
  const QUERY_PARAMS = {
    category: 'category',
    sort: 'sort',
  };

  const selectors = {
    filter: '.js-filter',
    menuTriangle: '.js-filter-menu-triangle',
    menuItem: '.js-filter-cat-item',
    catBtn: '.js-filter-cat-btn',
    catCounterWrapper: '.js-cat-counter-wrapper',
    catCounter: '.js-cat-counter',
    subcatsWrapper: '.js-filter-subcats-wrapper',
    subcatBtn: '.js-filter-subcat-btn',
    container: '.js-archive-blog-list',
    clearBtn: '.js-filter-clear-btn',
    errorMsg: '.js-error-msg',
    loader: '.js-product-loader',
    loadMoreBtn: '.js-load-more-button',
    loadMoreBtnLoader: '.js-load-more-btn-loader',
    gridBreak: '.js-grid-break',
    section: '.js-blog',
    post: '.js-blog-card',
  };

  const classes = {
    noEvents: 'ui-no-events',
    hide: 'ui-hide',
  };

  const section = document.querySelector(selectors.section);

  if (section === null) return;

  const { origin, pathname } = window.location;
  const baseURL = `${origin}${pathname}`;
  const separator = ',';

  const paramsValues = {
    [QUERY_PARAMS.category]: [],
    [QUERY_PARAMS.sort]: [],
  };

  const menuItems = document.querySelectorAll(selectors.menuItem);
  const container = document.querySelector(selectors.container);
  const filter = document.querySelector(selectors.filter);
  const clearBtn = document.querySelector(selectors.clearBtn);
  const loader = document.querySelector(selectors.loader);
  const errorMsg = document.querySelector(selectors.errorMsg);
  const loadMoreBtn = document.querySelector(selectors.loadMoreBtn);
  const loadMoreBtnLoader = loadMoreBtn?.querySelector(
    selectors.loadMoreBtnLoader
  );

  let loaderSetTimeoutID = null;
  let postsCount = container.querySelectorAll(selectors.post).length;
  let activeCatBtnItem = null;

  const getQueryParams = () => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });
    // Get the value of "some_key" in eg "https://example.com/?some_key=some_value"

    paramsValues.sort = params.sort?.split(separator) || [];
    paramsValues.category = params.category?.split(separator) || [];
  };

  const setNewURL = () => {
    const params = new URLSearchParams();

    const paramsKeys = Object.keys(paramsValues);

    paramsKeys.forEach((key) => {
      if (paramsValues[key].length > 0) {
        params.append(key, paramsValues[key]);
      }
    });

    const paramString = params.toString();
    const areParams = paramString !== '';
    const newURL = areParams ? `${baseURL}?${paramString}` : baseURL;
    toggleClass(clearBtn, { force: areParams });

    window.history.replaceState({}, '', newURL);
  };

  const createSearchParams = ({ postType, postsCount = 11, page = false }) => {
    const params = new URLSearchParams();

    params.append('action', 'filter_posts');
    params.append('post_type', postType);
    params.append('post_category', paramsValues.category);
    params.append('post_sort', paramsValues.sort);
    params.append('posts_count', postsCount);

    if (page !== false) {
      params.append('post_page', page);
    }

    return params;
  };

  const updatePostsCountVariable = ({ count, resetAllPostsVal = false }) => {
    postsCount = resetAllPostsVal ? count : postsCount + count;

    section.style.setProperty(
      '--posts',
      postsCount < 15 ? Math.ceil(postsCount / 3) : Math.floor(postsCount / 3)
    );
  };

  const filterPosts = async ({ taxonomy, postType }) => {
    const params = createSearchParams({ taxonomy, postType });

    try {
      const resp = await fetch('/wp-admin/admin-ajax.php', {
        method: 'POST',
        body: params,
      });

      // Get data and parse to JSON
      const { data, success } = await resp.json();
      const { count, html, pages, error } = data;

      if (!success) {
        errorMsg.textContent = error;
        toggleClass(loader, { force: false });
        toggleClass(errorMsg, { force: true });
        toggleClass(loadMoreBtn, { force: false });
        container.innerHTML = '';
      } else {
        // Toggle container inView class to force single product animation
        toggleClass(container, { className: STATE.inView, force: false });
        toggleClass(container, { force: true });
        toggleClass(loader, { force: false });
        updatePostsCountVariable({ count, resetAllPostsVal: true });
        setTimeout(
          () =>
            toggleClass(container, { className: STATE.inView, force: true }),
          100
        );
        container.innerHTML = html;

        toggleClass(loadMoreBtn, { force: pages > 1 });
        container.dataset.page = 1;
      }
    } catch (err) {
      clearTimeout(loaderSetTimeoutID);
      container.innerHTML = '';
      toggleClass(loadMoreBtn, { force: false });

      // Toggle loader visibility
      toggleClass(loader, { force: false });

      errorMsg.textContent = errorMsg.dataset.message;

      // Toggle error message visibility
      toggleClass(errorMsg, { force: true });
    }

    toggleClass(filter, { className: STATE.disabled, force: false });

    scroll.update();
  };

  const animateNewPosts = ({ page }) => {
    const newPosts = container.querySelectorAll(
      `[data-content-page="${page}"]`
    );

    newPosts.forEach((post) => post.classList.add(STATE.active));
  };

  const loadMorePosts = async ({ taxonomy, postType, page }) => {
    const params = createSearchParams({
      taxonomy,
      postType,
      page,
      postsCount: 11,
    });

    try {
      const resp = await fetch('/wp-admin/admin-ajax.php', {
        method: 'POST',
        body: params,
      });

      // Get data and parse to JSON
      const { data, success } = await resp.json();
      const { html, pages, count } = data;

      if (!success) return toggleClass(loadMoreBtn, { force: false });

      const breaks = [...container.querySelectorAll(selectors.gridBreak)];
      breaks.forEach((el) => container.removeChild(el));
      container.innerHTML += html;
      breaks.forEach((el) => container.appendChild(el));
      container.dataset.page = page;

      updatePostsCountVariable({ count, resetAllPostsVal: false });

      if (page === pages) {
        toggleClass(loadMoreBtn, { force: false });
      }

      // Move to the end of call stack to see the animation
      setTimeout(() => animateNewPosts({ page }), 0);
    } catch (err) {
      console.log(err);
    }

    toggleClass(loadMoreBtn, { className: classes.noEvents, force: false });
    toggleClass(loadMoreBtnLoader, { force: false });

    scroll.update();
  };

  const toggleCatParam = ({ cat, slug }) => {
    const categorySlugs = paramsValues[cat];

    const slugIndex = categorySlugs.findIndex((el) => el === slug);

    if (slugIndex !== -1) return categorySlugs.splice(slugIndex, 1);

    if (cat === QUERY_PARAMS.sort) {
      // Clear sort param array to keep only one value
      categorySlugs.length = 0;
    }

    categorySlugs.push(slug);
  };

  const initLoading = () => {
    // Hide error message
    toggleClass(errorMsg, { force: false });

    // Hide load more btn
    toggleClass(loadMoreBtn, { force: false });

    // Hide container
    toggleClass(container, { force: false });

    // Show loader after hiding container first
    loaderSetTimeoutID = setTimeout(
      () => toggleClass(loader, { force: true }),
      170
    );
  };

  const setCatCounter = ({ item, count }) => {
    const counterWrapper = item.querySelector(selectors.catCounterWrapper);
    const counter = counterWrapper.querySelector(selectors.catCounter);

    counter.textContent = count;

    toggleClass(counterWrapper, {
      className: classes.hide,
      force: count === 0,
    });
  };

  const resetCatCounters = () => {
    const counterWrappers = document.querySelectorAll(
      selectors.catCounterWrapper
    );

    if (counterWrappers.length === 0) return;

    counterWrappers.forEach((counterWrapper) => {
      // Hide counter wrappers
      toggleClass(counterWrapper, { className: classes.hide, force: true });

      const counter = counterWrapper.querySelector(selectors.catCounter);
      // Reset counter to 0
      counter.textContent = 0;
    });
  };

  const handleCatBtnClick = ({ item }) => {
    if (window.innerWidth >= breakpoints.tablet) return;

    // Toggle active class for current btn
    toggleClass(item, { className: STATE.active });

    if (activeCatBtnItem === item) return;

    // Remove active class from previous btn
    toggleClass(activeCatBtnItem, { className: STATE.active, force: false });

    // Update active cat btn variable
    activeCatBtnItem = item;
  };

  const handleSubCatBtnClick = (item, e) => {
    const { currentTarget: target } = e;
    const { taxonomy, postType, slug } = target.dataset;
    const { cat } = item.dataset;

    toggleClass(filter, { className: STATE.disabled, force: true });

    toggleCatParam({ cat, slug });

    const catCount = paramsValues[cat].length;

    setCatCounter({ item, count: catCount });
    setNewURL();

    initLoading();
    filterPosts({ taxonomy, postType });
  };

  const addSubCatBtnListener = (item) => {
    const subCatBtns = item.querySelectorAll(selectors.subcatBtn);

    subCatBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => handleSubCatBtnClick(item, e));
    });
  };

  const setTrianglePosition = (item) => {
    if (window.innerWidth < breakpoints.tablet) return;

    const menuTriangle = item.querySelector(selectors.menuTriangle);
    const catBtn = item.querySelector(selectors.catBtn);

    const catBtnOffsetLeft = catBtn.offsetLeft;
    const catBtnWidth = catBtn.clientWidth;
    const catBtnTranslate = catBtnOffsetLeft + catBtnWidth * 0.35;

    const iconWidth = 36;

    menuTriangle.style.left = `calc(${catBtnTranslate}px - ${
      iconWidth * 0.5
    }px)`;
  };

  const addCatBtnClickListener = (item) => {
    const catBtn = item.querySelector(selectors.catBtn);

    catBtn.addEventListener('click', (e) => handleCatBtnClick({ e, item }));
  };

  const addCatBtnsClickListeners = () => {
    menuItems.forEach(addCatBtnClickListener);
  };

  const setTrianglesPositions = () => {
    menuItems.forEach(setTrianglePosition);
  };

  const addSubCatBtnsListeners = () => {
    menuItems.forEach(addSubCatBtnListener);
  };

  const handleLoadMoreBtnClick = (e) => {
    const { currentTarget: target } = e;
    const { taxonomy, postType } = target.dataset;
    const { page } = container.dataset;

    toggleClass(target, { className: classes.noEvents, force: true });
    toggleClass(loadMoreBtnLoader, { force: true });

    const nextPage = parseInt(page, 10) + 1;

    loadMorePosts({ taxonomy, postType, page: nextPage });
  };

  const handleBodyClick = (e) => {
    if (window.innerWidth >= breakpoints.tablet) return;

    const isClickedWithinMenuItem = e.target.closest(selectors.menuItem);

    if (isClickedWithinMenuItem !== null) return;

    toggleClass(activeCatBtnItem, { force: false });
  };

  const handleClearBtnClick = (e) => {
    const { taxonomy, postType } = e.currentTarget.dataset;

    initLoading();

    // Clear all query param values
    Object.keys(paramsValues).forEach((key) => (paramsValues[key].length = 0));

    // Set URL without params
    setNewURL();

    // Reset all category filter counters
    resetCatCounters();

    // Fetch posts
    filterPosts({ taxonomy, postType });
  };

  // Call methods
  getQueryParams();
  setTrianglesPositions();
  addCatBtnsClickListeners();
  addSubCatBtnsListeners();

  loadMoreBtn?.addEventListener('click', handleLoadMoreBtnClick);
  clearBtn?.addEventListener('click', handleClearBtnClick);
  window.addEventListener('resize', debounce(setTrianglesPositions, 300));
  document.body.addEventListener('click', handleBodyClick);
};

export default handleRecipeFilter;
